import React, { useMemo, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

export default function withRouter(Component) {
  function ComponentWithRouter(props) {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    const listenersRef = useRef([]);

    useEffect(() => {
      listenersRef.current.forEach((fn) => {
        try {
          fn(location);
        } catch (e) {
          // ignore listener error
        }
      });
    }, [location]);

    const history = useMemo(
      () => ({
        push: (to, state) => {
          if (typeof to === 'object' && to.pathname) {
            navigate(to.pathname + (to.search || ''), {
              state: to.state || state,
            });
          } else {
            navigate(to, { state });
          }
        },
        replace: (to, state) => {
          if (typeof to === 'object' && to.pathname) {
            navigate(to.pathname + (to.search || ''), {
              replace: true,
              state: to.state || state,
            });
          } else {
            navigate(to, { replace: true, state });
          }
        },
        goBack: () => navigate(-1),
        goForward: () => navigate(1),
        go: (n) => navigate(n),
        location,
        listen: (listener) => {
          listenersRef.current.push(listener);
          return () => {
            listenersRef.current = listenersRef.current.filter(
              (l) => l !== listener
            );
          };
        },
        createHref: (to) => (typeof to === 'string' ? to : to.pathname || ''),
      }),
      [navigate, location]
    );

    const match = useMemo(
      () => ({
        params,
        isExact: true,
        path: '',
        url: location.pathname,
      }),
      [params, location.pathname]
    );

    return (
      <Component
        {...props}
        history={props.history || history}
        location={props.location || location}
        match={props.match || match}
        params={props.params || params}
        navigate={props.navigate || navigate}
      />
    );
  }

  const displayName = Component.displayName || Component.name || 'Component';
  ComponentWithRouter.displayName = `withRouter(${displayName})`;

  return ComponentWithRouter;
}
